import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { Subject, Subscription } from 'rxjs';
import { AttributeCategoryModel, AttributeSelectorModel, AttributeType, AttributeValueModel, SelectedCategoryModel } from 'src/shared/models/attribute-selector.models';
import { CXOneMultiSelectModel } from 'src/shared/models/cxone-components-models';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { AttributeSelectorService } from '../attribute-selector/attribute-selector.service';
import { CvConfigurationService } from '../services/cv-configuration.service';

@Component({
    selector: 'cv-single-attribute-selector',
    templateUrl: './single-attribute-selector.component.html',
    styleUrls: ['./single-attribute-selector.component.scss']
})
export class SingleAttributeSelectorComponent implements OnInit, OnChanges, OnDestroy {
    @Input() settingsManager: SlideoutSettingsManager;
    @Input() attributeType = AttributeType.Data;
    @Input() attributeName: string;
    @Input() pageName: string;
    @Input() autoWidth = true;
    @ViewChild(MatSelect) matSelect: MatSelect;

    category: AttributeCategoryModel;
    values: AttributeValueModel[] = [];

    loadedModelType: AttributeType = null;
    selectorModel: AttributeSelectorModel;
    categoryModel: SelectedCategoryModel = null;
    allSelected = false;
    filter = '';
    savePromise: Promise<any> = null;
    subscriptions: Subscription[] = [];
    valuesModel: (AttributeValueModel & CXOneMultiSelectModel)[] = [];
    lastSavedIds: Set<number> = new Set();

    private _openPromise: Promise<void> = null;
    private _resolveOpenPromise: () => void = null;

    constructor(
        public service: AttributeSelectorService,
        public config: CvConfigurationService,
        private cd: ChangeDetectorRef,
    ) { }

    ngOnInit() {
        
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.settingsManager) {
            this.unsubscribeEvents();
            this.subscriptions.push(this.settingsManager?.beforeSettingsApplied.subscribe(() => {
                if (this._openPromise) {
                    this.settingsManager?.waitBeforeApplying.push(this._openPromise);
                    this.matSelect?.close();
                } 
                else if  (this.savePromise)
                    this.settingsManager?.waitBeforeApplying.push(this.savePromise);
            }));
        }

        if (changes.attributeName?.currentValue || (this.attributeName && changes.attributeType)) {
            this.updateSelectedAttribute();
            this.cd.detectChanges();
        }

        if (changes.attributeType?.currentValue) {
            this.loadAttributeModel();
            this.cd.detectChanges();
        }
    }

    ngOnDestroy() {
        this.unsubscribeEvents();
    }

    unsubscribeEvents() {
        this.subscriptions?.forEach(s => s?.unsubscribe());
    }

    onSelectorOpen() {
        this._openPromise = new Promise(resolve => this._resolveOpenPromise = resolve);
    }

    onSelectorClose() {
        // If no attribute is selected for the currently selected category, select all attributes
        if (this.categoryModel.SelectedAttributeIDs?.length === 0)
            this.onSelectAllToggled();

        this.save();
    }

    onSelectAllToggled() {
        if (!this.values)
            return; // values have not loaded yet

        if (!this.allSelected) {
            this.categoryModel.SelectedAttributeIDs = [-1].concat(this.values.map(val => val.ID));
        } else {
            this.categoryModel.SelectedAttributeIDs = [];
        }

        this.updateValuesModel();
        this.allSelected = !this.allSelected;
    }

    onSelectedAttributeValueChanged(change: MatOptionSelectionChange) {
        if (!change.isUserInput)
            return;

        if (!this.values)
            return; // values have not loaded yet

        const valueId: number = change.source.value;
        const index = this.categoryModel.SelectedAttributeIDs.indexOf(valueId);

        if (index >= 0)
            this.categoryModel.SelectedAttributeIDs.splice(index, 1);
        else
            this.categoryModel.SelectedAttributeIDs.push(valueId);

        // unselect the "All" option when not all are selected
        const selectedValuesWithoutAll = this.categoryModel.SelectedAttributeIDs.filter(id => id !== -1);
        if (selectedValuesWithoutAll.length !== this.values.length) {
            this.categoryModel.SelectedAttributeIDs = selectedValuesWithoutAll;
            this.allSelected = false;
        } else { // all options selected, make sure all is checked
            this.categoryModel.SelectedAttributeIDs = [-1].concat(selectedValuesWithoutAll);
            this.allSelected = true;
        }

        this.updateValuesModel();
    }

    hasSelectionChanged() {
        // check if any ids have been added to the model
        const newIds = this.categoryModel.SelectedAttributeIDs.filter(id => !this.lastSavedIds.has(id));
        if (newIds.length > 0)
            return true;

        // check if any ids have been removed from the model
        const currentSet = new Set(this.categoryModel.SelectedAttributeIDs);
        for (const id of this.lastSavedIds) {
            if (!currentSet.has(id))
                return true;
        }
        return false;
    }

    updateValuesModel() {
        const selectedIds = new Set(this.categoryModel.SelectedAttributeIDs);
        const hasAll = selectedIds.has(-1);
        this.valuesModel = this.values?.
            filter(({ID}) => hasAll || selectedIds.has(ID)).
            map(({ID, Name}) => ({ ID, Name, selected: true }));
    }

    async loadAttributeModel(force = false) {
        if (!force && this.loadedModelType === this.attributeType)
            return;

        this.loadedModelType = this.attributeType;
        this.selectorModel = await this.service.loadSelectorModel(this.pageName, this.attributeType);
        this.updateCategoryModel();
    }

    async updateSelectedAttribute() {
        const categories = await this.service.loadCategories(this.attributeType);
        const category = categories.AttributeCategoryModels.find(c =>
            c.CategoryName.localeCompare(this.attributeName, undefined, { sensitivity: 'base' }) === 0
        );

        if (category) {
            this.category = category;
            const values = await this.service.loadValues(this.category.CategoryID, this.attributeType);
            values.unshift({ ID: 0, Name: this.service.localizationText.notSet });
            this.values = values;

            if (!this.selectorModel) {
                await this.loadAttributeModel();
            } else {
                this.updateCategoryModel();
            }
        } else {
            this.category = null;
            this.values = null;
            this.categoryModel = null;
        }
    }

    async save() {
        if (!this.selectorModel)
            return;

        this.lastSavedIds = new Set(this.categoryModel.SelectedAttributeIDs);
        try {
            const index = this.selectorModel.SelectedCategories.findIndex(cat => cat.SelectedCategoryID == this.category.CategoryID);
            this.selectorModel.SelectedCategories[index] = this.categoryModel;
            this.savePromise = this.service.saveSelectorModel(this.pageName, this.selectorModel);
            await this.savePromise;
        } finally {
            this._resolveOpenPromise?.();
            this._resolveOpenPromise = null;
            this._openPromise = null;
        }
    }

    updateCategoryModel() {
        if (!this.selectorModel || !this.category || !this.values || this.values.length < 1)
            return;

        this.categoryModel = this.selectorModel.SelectedCategories.find(c => c.SelectedCategoryID === this.category.CategoryID);
        if (this.categoryModel) {
            this.allSelected = this.categoryModel.SelectedAttributeIDs.length === this.values.length + 1;
        } else {
            this.categoryModel = {
                SelectedCategoryID: this.category.CategoryID,
                SelectedAttributeIDs: [-1].concat(this.values.map(val => val.ID)),
            };
            this.selectorModel.SelectedCategories.push(this.categoryModel);
            this.allSelected = true;
            this.save();
        }

        this.lastSavedIds = new Set([...this.categoryModel.SelectedAttributeIDs]);
        this.updateValuesModel();
    }
}
